// Upload pipeline for supplier files (2026-09-16).
//
// Two problems this solves:
//
// 1. Phone photos are 4–12 MB each. Base64 makes them a third bigger again,
//    and the old submit form held the PDF plus up to five of them in memory at
//    once, then JSON.stringify'd the lot — on iOS Safari that is the "the page
//    crashed" report. Photos are now resized to at most 2000 px on the long
//    edge and re-encoded as JPEG before upload; a 9 MB photo becomes ~600 KB.
//
// 2. Everything travelled inside one JSON POST. Now each file goes up on its
//    own (api_uploadFile → Drive file id) and the final submit carries ids
//    only, so memory stays flat, a single slow file can't sink the whole
//    submission, and the backend never has to decode six files inside a lock.

import { api, fileToBase64 } from './api.js';

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const COMPRESS_ABOVE = 800 * 1024;    // leave small images untouched
const MAX_EDGE = 2000;                // px on the long edge
const JPEG_QUALITY = 0.85;

/**
 * Shrink an oversized photo. Returns the original File when it isn't a
 * (decodable) image, is already small, or anything about decoding fails —
 * the upload must never be blocked by the optimisation.
 */
export async function compressImage(file) {
  if (!file || !IMAGE_TYPES.includes(file.type) || file.size <= COMPRESS_ABOVE) return file;
  try {
    const bitmap = await loadBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    // PNG with transparency → flatten on white so the JPEG doesn't go black.
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);
    if (bitmap.close) bitmap.close();
    const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', JPEG_QUALITY));
    if (!blob || blob.size >= file.size) return file;   // no gain — keep the original
    const name = file.name.replace(/\.(png|webp|jpeg|jpg)$/i, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified });
  } catch (_) {
    return file;
  }
}

function loadBitmap(file) {
  // createImageBitmap honours EXIF orientation in current browsers; fall back
  // to an <img> where it's missing (older Safari).
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file).catch(() => loadViaImg(file));
  }
  return loadViaImg(file);
}

function loadViaImg(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image decode failed')); };
    img.src = url;
  });
}

/**
 * Upload files one at a time. `purpose` is 'invoice-pdf' | 'pop' | 'tax'.
 * `onProgress({ index, total, name, phase })` fires before each file
 * (phase 'compress' then 'upload'). Resolves to the backend's
 * [{ id, name, size, mime }] in the same order.
 */
export async function uploadFiles(files, purpose, onProgress) {
  const out = [];
  const total = files.length;
  for (let i = 0; i < total; i++) {
    let f = files[i];
    onProgress?.({ index: i + 1, total, name: f.name, phase: 'compress' });
    if (purpose !== 'invoice-pdf') f = await compressImage(f);
    onProgress?.({ index: i + 1, total, name: f.name, phase: 'upload' });
    const base64 = await fileToBase64(f);
    const meta = await api.uploadFile({ purpose, base64, filename: f.name });
    out.push(meta);
  }
  return out;
}
